/**
 * Collections List - AJAX Pagination with Search & Sort
 */
(function() {
  'use strict';

  var section = document.querySelector('[data-collections-section]');
  if (!section) return;

  var grid = section.querySelector('[data-collections-grid]');
  var skeletons = section.querySelector('[data-skeletons]');
  var pagination = section.querySelector('[data-pagination]');
  var prevBtn = section.querySelector('[data-page-prev]');
  var nextBtn = section.querySelector('[data-page-next]');
  var pageNumbers = section.querySelector('[data-page-numbers]');
  var currentPageEl = section.querySelector('[data-current-page]');
  var totalPagesEl = section.querySelector('[data-total-pages]');

  // Filter bar elements
  var searchInput = section.querySelector('[data-collection-search]');
  var searchClear = section.querySelector('[data-search-clear]');
  var sortSelect = section.querySelector('[data-sort-select]');
  var totalCountEl = section.querySelector('[data-total-count]');
  var noResultsEl = section.querySelector('[data-no-results]');
  var searchTermEl = section.querySelector('[data-search-term]');
  var clearSearchBtn = section.querySelector('[data-clear-search]');

  var currentPage = 1;
  var totalPages = parseInt(totalPagesEl?.textContent || '1');
  var isLoading = false;
  var searchTimeout = null;

  // Show skeletons, hide grid
  function showLoading() {
    if (grid) grid.style.display = 'none';
    if (skeletons) {
      skeletons.style.display = 'grid';
      skeletons.style.gridTemplateColumns = grid ? getComputedStyle(grid).gridTemplateColumns : 'repeat(4, 1fr)';
    }
    isLoading = true;
  }

  // Hide skeletons, show grid
  function hideLoading() {
    if (skeletons) skeletons.style.display = 'none';
    if (grid) {
      grid.style.display = 'grid';
      grid.style.opacity = '1';
    }
    isLoading = false;
  }

  // Update pagination UI
  function updatePaginationUI(page) {
    currentPage = page;

    // Update current page display
    if (currentPageEl) currentPageEl.textContent = page;

    // Update prev/next buttons
    if (prevBtn) prevBtn.disabled = page <= 1;
    if (nextBtn) nextBtn.disabled = page >= totalPages;

    // Update page number buttons
    if (pageNumbers) {
      var buttons = pageNumbers.querySelectorAll('[data-page]');
      buttons.forEach(function(btn) {
        var btnPage = parseInt(btn.dataset.page);
        btn.classList.toggle('is-active', btnPage === page);
        btn.setAttribute('aria-current', btnPage === page ? 'page' : 'false');
      });
    }

    // Update URL without reload
    var url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    history.pushState({ page: page }, '', url.toString());
  }

  // Fetch page via AJAX
  function loadPage(page) {
    if (isLoading || page < 1 || page > totalPages || page === currentPage) return;

    showLoading();

    var url = new URL(window.location.href);
    url.searchParams.set('page', page);

    fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(function(response) {
      return response.text();
    })
    .then(function(html) {
      // Parse the response
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var newGrid = doc.querySelector('[data-collections-grid]');
      var newPagination = doc.querySelector('[data-pagination]');
      var newViewing = doc.querySelector('[data-viewing]');

      // Update grid content while hidden
      if (newGrid && grid) {
        grid.innerHTML = newGrid.innerHTML;
      }

      // Update pagination
      if (newPagination && pagination) {
        var newPageNumbers = newPagination.querySelector('[data-page-numbers]');
        if (newPageNumbers && pageNumbers) {
          pageNumbers.innerHTML = newPageNumbers.innerHTML;
          // Re-bind page number click events
          bindPageNumberClicks();
        }
      }

      // Update viewing text
      var viewingEl = section.querySelector('[data-viewing]');
      if (newViewing && viewingEl) {
        viewingEl.textContent = newViewing.textContent;
      }

      updatePaginationUI(page);
      hideLoading();

      // Scroll to top of section smoothly
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    })
    .catch(function(error) {
      console.error('Error loading page:', error);
      hideLoading();
    });
  }

  // Bind click events to page number buttons
  function bindPageNumberClicks() {
    var buttons = section.querySelectorAll('[data-page]');
    buttons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var page = parseInt(this.dataset.page);
        loadPage(page);
      });
    });
  }

  // Event listeners
  if (prevBtn) {
    prevBtn.addEventListener('click', function() {
      loadPage(currentPage - 1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', function() {
      loadPage(currentPage + 1);
    });
  }

  // Initial bind for page numbers
  bindPageNumberClicks();

  // Handle browser back/forward
  window.addEventListener('popstate', function(e) {
    if (e.state && e.state.page) {
      loadPage(e.state.page);
    } else {
      // Check URL for page param
      var url = new URL(window.location.href);
      var page = parseInt(url.searchParams.get('page')) || 1;
      if (page !== currentPage) {
        loadPage(page);
      }
    }
  });

  // Set initial page from URL
  var initialUrl = new URL(window.location.href);
  var initialPage = parseInt(initialUrl.searchParams.get('page')) || 1;
  if (initialPage > 1) {
    currentPage = initialPage;
    updatePaginationUI(initialPage);
  }

  // ========================================
  // Search & Sort Functionality
  // ========================================

  // Fuzzy search - check if search matches with flexibility
  function fuzzyMatch(title, searchTerm) {
    if (!searchTerm) return true;

    // Direct include check
    if (title.includes(searchTerm)) return true;

    // Split title by common separators and check each part
    var titleParts = title.split(/[\s\/\-\_\|\,\.]+/);
    for (var i = 0; i < titleParts.length; i++) {
      if (titleParts[i].includes(searchTerm)) return true;
      if (searchTerm.includes(titleParts[i]) && titleParts[i].length > 1) return true;
    }

    // Check if search term matches start of any word
    for (var j = 0; j < titleParts.length; j++) {
      if (titleParts[j].startsWith(searchTerm)) return true;
    }

    // Remove non-alphanumeric and check
    var cleanTitle = title.replace(/[^a-z0-9]/g, '');
    var cleanSearch = searchTerm.replace(/[^a-z0-9]/g, '');
    if (cleanTitle.includes(cleanSearch)) return true;

    // Levenshtein-like: check if only 1-2 chars different for short terms
    if (searchTerm.length >= 3) {
      for (var k = 0; k < titleParts.length; k++) {
        var part = titleParts[k];
        if (part.length >= 3 && similarEnough(part, searchTerm)) return true;
      }
    }

    return false;
  }

  // Simple similarity check - allows 1-2 char difference
  function similarEnough(str1, str2) {
    if (Math.abs(str1.length - str2.length) > 2) return false;

    var longer = str1.length > str2.length ? str1 : str2;
    var shorter = str1.length > str2.length ? str2 : str1;

    // Check if shorter is substring
    if (longer.includes(shorter)) return true;

    // Count matching chars
    var matches = 0;
    for (var i = 0; i < shorter.length; i++) {
      if (longer.indexOf(shorter[i]) !== -1) matches++;
    }

    // At least 70% chars match
    return matches / shorter.length >= 0.7;
  }

  // Filter collections by search term
  function filterCollections(searchTerm) {
    var cards = grid.querySelectorAll('[data-collection-card]');
    var visibleCount = 0;
    var term = searchTerm.toLowerCase().trim();

    cards.forEach(function(card) {
      var title = card.dataset.title || '';
      var isMatch = fuzzyMatch(title, term);

      if (isMatch) {
        card.classList.remove('is-hidden');
        visibleCount++;
      } else {
        card.classList.add('is-hidden');
      }
    });

    // Update count
    if (totalCountEl) {
      totalCountEl.textContent = visibleCount + ' categor' + (visibleCount === 1 ? 'y' : 'ies');
    }

    // Show/hide no results
    if (noResultsEl) {
      if (visibleCount === 0 && term) {
        noResultsEl.style.display = 'flex';
        if (searchTermEl) searchTermEl.textContent = searchTerm;
        if (grid) grid.style.display = 'none';
        if (pagination) pagination.style.display = 'none';
      } else {
        noResultsEl.style.display = 'none';
        if (grid) grid.style.display = 'grid';
        if (pagination && totalPages > 1) pagination.style.display = 'flex';
      }
    }
  }

  // Sort collections
  function sortCollections(sortBy) {
    var cards = Array.from(grid.querySelectorAll('[data-collection-card]'));

    cards.sort(function(a, b) {
      var titleA = a.dataset.title || '';
      var titleB = b.dataset.title || '';
      var productsA = parseInt(a.dataset.products) || 0;
      var productsB = parseInt(b.dataset.products) || 0;

      switch (sortBy) {
        case 'alphabetical':
          return titleA.localeCompare(titleB);
        case 'alphabetical-desc':
          return titleB.localeCompare(titleA);
        case 'products-high':
          return productsB - productsA;
        case 'products-low':
          return productsA - productsB;
        default:
          return 0;
      }
    });

    // Re-append sorted cards
    cards.forEach(function(card) {
      grid.appendChild(card);
    });
  }

  // Clear search
  function clearSearch() {
    if (searchInput) {
      searchInput.value = '';
      filterCollections('');
    }
  }

  // Search input handler
  if (searchInput) {
    searchInput.addEventListener('input', function() {
      clearTimeout(searchTimeout);
      var value = this.value;

      searchTimeout = setTimeout(function() {
        filterCollections(value);
      }, 200);
    });
  }

  // Search clear button
  if (searchClear) {
    searchClear.addEventListener('click', clearSearch);
  }

  // Clear search button in no results
  if (clearSearchBtn) {
    clearSearchBtn.addEventListener('click', clearSearch);
  }

  // Sort select handler
  if (sortSelect) {
    sortSelect.addEventListener('change', function() {
      sortCollections(this.value);
    });
  }

})();
